import React from "react";
import { Calculator, Sparkles, ExternalLink } from "lucide-react";

/**
 * Cross-promotion section linking to Fitness&me partner tools.
 * Uses brand-exact colours (lime / cyan / purple) on a dark background.
 */
export default function FitnessandPromo({ lang }) {
  return (
    <section className="container-page pb-12" data-testid="fitnessand-links">
      <div className="rounded-3xl p-7 sm:p-10" style={{ backgroundColor: "#0a0e14" }}>
        <div className="mb-7">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{ backgroundColor: "#262c36", color: "#d4d7e0" }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#cafd00" }} />
            {lang === "et"
              ? "VEEL KASULIKKE TÖÖRIISTU"
              : lang === "ru"
              ? "ЕЩЁ ПОЛЕЗНЫЕ ИНСТРУМЕНТЫ"
              : "MORE USEFUL TOOLS"}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            <span style={{ color: "#cafd00" }}>Fitness</span>
            <span style={{ color: "#f1f3fc" }}>&amp;</span>
            <span style={{ color: "#00f1fe" }}>me</span>
          </h2>
          <p className="text-[15px] mt-2 max-w-xl leading-relaxed" style={{ color: "#d4d7e0" }}>
            {lang === "et"
              ? "Meie partnerlehel leiad tehisintellektiga tervise- ja vormi tööriistu."
              : lang === "ru"
              ? "На нашем партнёрском сайте вы найдёте инструменты ИИ для здоровья и формы."
              : "On our partner site you will find AI-powered health and fitness tools."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <a
            href="https://fitnessand.me/fatai"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fitnessand-fatai-link"
            className="group relative rounded-[24px] p-6 transition-all overflow-hidden"
            style={{ backgroundColor: "#1b2028" }}
          >
            <div
              className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none opacity-40 blur-3xl"
              style={{ backgroundColor: "#d277ff" }}
            />
            <div className="relative flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: "rgba(210, 119, 255, 0.15)", color: "#d277ff" }}
              >
                <Sparkles className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-[18px] font-bold tracking-tight" style={{ color: "#f1f3fc" }}>
                    Fat%AI
                  </h3>
                  <span
                    className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest"
                    style={{ color: "#d277ff", backgroundColor: "rgba(210, 119, 255, 0.15)" }}
                  >
                    AI
                  </span>
                </div>
                <p className="text-[14px] leading-relaxed mb-4" style={{ color: "#d4d7e0" }}>
                  {lang === "et"
                    ? "Tehisintellektil põhinev kehakoostise ja rasvaprotsendi hindamine fotost."
                    : lang === "ru"
                    ? "Оценка состава тела и процента жира по фотографии на основе ИИ."
                    : "AI-powered body composition and body-fat percentage assessment from a photo."}
                </p>
                <div
                  className="inline-flex items-center gap-1.5 text-[12px] font-bold group-hover:gap-2 transition-all"
                  style={{ color: "#d277ff" }}
                >
                  fitnessand.me/fatai
                  <ExternalLink className="h-3.5 w-3.5" />
                </div>
              </div>
            </div>
          </a>

          <a
            href="https://fitnessand.me/calculators"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fitnessand-calculators-link"
            className="group relative rounded-[24px] p-6 transition-all overflow-hidden"
            style={{ backgroundColor: "#1b2028" }}
          >
            <div
              className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none opacity-40 blur-3xl"
              style={{ backgroundColor: "#cafd00" }}
            />
            <div className="relative flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: "rgba(202, 253, 0, 0.15)", color: "#cafd00" }}
              >
                <Calculator className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-[18px] font-bold tracking-tight" style={{ color: "#f1f3fc" }}>
                    {lang === "et" ? "Kalkulaatorid" : lang === "ru" ? "Калькуляторы" : "Calculators"}
                  </h3>
                </div>
                <p className="text-[14px] leading-relaxed mb-4" style={{ color: "#d4d7e0" }}>
                  {lang === "et"
                    ? "Kalorivajadus, makrod, treeningmaht ja teised vormi puudutavad arvutused."
                    : lang === "ru"
                    ? "Расчёт калорий, макроэлементов, объёма тренировок и других показателей формы."
                    : "Calorie needs, macros, training volume and other fitness-related calculators."}
                </p>
                <div
                  className="inline-flex items-center gap-1.5 text-[12px] font-bold group-hover:gap-2 transition-all"
                  style={{ color: "#cafd00" }}
                >
                  fitnessand.me/calculators
                  <ExternalLink className="h-3.5 w-3.5" />
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
